/**
 * Story 8.12 — Onglet Bancaire en lecture seule (modale profil membre)
 * Affiche les coordonnées de contact et les infos bancaires.
 * IBAN masqué (seuls les 4 derniers caractères via masked_iban).
 * Pas de boutons d'édition.
 */
import React from 'react';
import type { ProfileData } from '../../../../../services/profileService';
import SectionPliable from '../../../../../composants/SectionPliable/SectionPliable';

interface ReadOnlyBancaireProps {
    data: ProfileData;
}

const formatPhone = (phone?: string | null): string => {
    if (!phone) return '-';
    const digits = phone.replace(/\D/g, '');
    let n = digits;
    if (digits.startsWith('33') && digits.length === 11) n = digits.slice(2);
    else if (digits.startsWith('0') && digits.length === 10) n = digits.slice(1);
    else return phone;
    return `+33 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`;
};

const formatEmail = (email?: string | null): string => email ? email.toLowerCase() : '-';

const ReadOnlyBancaire: React.FC<ReadOnlyBancaireProps> = ({ data }) => {
    const { coordonnees, bancaire } = data;

    return (
        <div className="readonly-tab readonly-bancaire">
            {/* Contact comptabilité */}
            <SectionPliable title="Contact comptabilité" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    <div className="readonly-contact-section">
                        <div className="readonly-grid three-cols">
                            <div className="readonly-field">
                                <label>Nom</label>
                                <p>{coordonnees?.compta_nom || '-'}</p>
                            </div>
                            <div className="readonly-field">
                                <label>Prénom</label>
                                <p>{coordonnees?.compta_prenom || '-'}</p>
                            </div>
                            <div className="readonly-field">
                                <label>Fonction</label>
                                <p>{coordonnees?.compta_fonction || '-'}</p>
                            </div>
                        </div>
                        <div className="readonly-grid three-cols">
                            <div className="readonly-field">
                                <label>Email</label>
                                <p>{formatEmail(coordonnees?.compta_email)}</p>
                            </div>
                            <div className="readonly-field">
                                <label>Téléphone</label>
                                <p>{formatPhone(coordonnees?.compta_tel)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </SectionPliable>

            {/* Contact facturation */}
            <SectionPliable title="Contact facturation" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    <div className="readonly-contact-section">
                        <div className="readonly-grid three-cols">
                            <div className="readonly-field">
                                <label>Nom</label>
                                <p>{coordonnees?.facture_nom || '-'}</p>
                            </div>
                            <div className="readonly-field">
                                <label>Prénom</label>
                                <p>{coordonnees?.facture_prenom || '-'}</p>
                            </div>
                            <div className="readonly-field">
                                <label>Fonction</label>
                                <p>{coordonnees?.facture_fonction || '-'}</p>
                            </div>
                        </div>
                        <div className="readonly-grid three-cols">
                            <div className="readonly-field">
                                <label>Email</label>
                                <p>{formatEmail(coordonnees?.facture_email)}</p>
                            </div>
                            <div className="readonly-field">
                                <label>Téléphone</label>
                                <p>{formatPhone(coordonnees?.facture_tel)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </SectionPliable>

            {/* Coordonnées bancaires */}
            <SectionPliable title="Coordonnées bancaires" className="readonly-section-pliable">
                <div className="readonly-section-content">
                    {!bancaire ? (
                        <p className="readonly-empty">Aucune coordonnée bancaire renseignée.</p>
                    ) : (
                        <div className="readonly-grid three-cols">
                            <div className="readonly-field">
                                <label>Titulaire du compte</label>
                                <p>{bancaire.nom_titulaire || '-'}</p>
                            </div>
                            <div className="readonly-field">
                                <label>Banque</label>
                                <p>{bancaire.banque || '-'}</p>
                            </div>
                            <div className="readonly-field">
                                <label>IBAN</label>
                                <p className="readonly-iban-masked">{bancaire.masked_iban || '****'}</p>
                            </div>
                            <div className="readonly-field">
                                <label>BIC</label>
                                <p>{bancaire.BIC || '-'}</p>
                            </div>
                            <div className="readonly-field">
                                <label>N° TVA intracommunautaire</label>
                                <p>{bancaire.numero_tva || '-'}</p>
                            </div>
                        </div>
                    )}
                </div>
            </SectionPliable>
        </div>
    );
};

export default ReadOnlyBancaire;
